import React from 'react';
import { LoadPort } from './LoadPort';
import { Conveyor } from './Conveyor';
import { CleanroomWorkcell3D } from '../CleanroomWorkcell/CleanroomWorkcell3D';
import { AmhsInteractiveGroup, AmhsLabel } from '../shared';
import type { AMHSEquipmentType, AmhsEquipmentTint, EquipmentStatus } from '../types';
import type { EquipmentPortVisual, Vec3 } from './equipmentPortTransfer';
import type { LayoutElement } from '../../../../types';

export type AMHSEquipmentProps = {
  type: AMHSEquipmentType;
  width?: number;
  height?: number;
  depth?: number;
  /** Load port / conveyor FOUP presence. */
  foupPresent?: boolean;
  /**
   * Process equipment: how many front load ports (drop/collect).
   * Defaults to 2 for AMHS_INTERFACE.
   */
  loadPortCount?: number;
  /** Process equipment: which load-port indices hold a FOUP. */
  occupiedLoadPorts?: number[];
  /** Along-dock fractions from the 2D ports so 3D seats sit under the same spots. */
  portFractions?: readonly number[];
  /** Per-port shutter + transfer state (independent). */
  portVisuals?: EquipmentPortVisual[];
  flyingFoup?: { position: Vec3; scaleHint?: number } | null;
  isTransferring?: boolean;
  transferProgress01?: number;
  transferOperation?: 'PICKUP' | 'DROP';
  status?: EquipmentStatus;
  showLabel?: boolean;
  label?: string;
  selected?: boolean;
  onSelect?: () => void;
  opacity?: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
  /** Family colour — buffers, lifters and tools share the same shell geometry. */
  tint?: AmhsEquipmentTint;
  /** Layout element for live transfer animation (process tools). */
  layoutElement?: LayoutElement | null;
};

/**
 * Reusable AMHS equipment shell.
 * Layout flow: AMHS_INTERFACE (process tool + load ports), LOAD_PORT, CONVEYOR.
 */
export const AMHSEquipment: React.FC<AMHSEquipmentProps> = ({
  type,
  width = 2,
  height = 2.2,
  depth = 1.5,
  foupPresent = false,
  loadPortCount = 2,
  occupiedLoadPorts,
  portFractions,
  portVisuals,
  flyingFoup = null,
  isTransferring = false,
  transferProgress01 = 0,
  transferOperation = 'PICKUP',
  status = 'IDLE',
  showLabel = false,
  label,
  selected,
  onSelect,
  opacity = 1,
  position,
  rotation,
  scale,
  tint,
  layoutElement = null,
}) => {
  const resolvedLabel = label ?? type;
  // The process cabinet carries an EQ nameplate on its upper front panel, so the
  // status chip sits beside it. Other shells have no plate to align to.
  const hasNameplate = type !== 'LOAD_PORT' && type !== 'CONVEYOR';
  const labelPosition: [number, number, number] = hasNameplate
    ? [-width * 0.08, height * 0.865, depth * 0.3]
    : [0, height * 0.74, depth * 0.58];
  const statusLabel =
    status === 'RUNNING'
      ? `${resolvedLabel} · PROC`
      : status === 'OFFLINE'
        ? `${resolvedLabel} · OFF`
        : status === 'IDLE'
          ? `${resolvedLabel} · IDLE`
          : resolvedLabel;

  let body: React.ReactNode;
  switch (type) {
    case 'LOAD_PORT':
      body = (
        <LoadPort
          width={width}
          height={height}
          depth={depth}
          foupPresent={foupPresent}
          portFractions={portFractions}
          portVisual={portVisuals?.[0] ?? null}
          flyingFoup={flyingFoup}
          isTransferring={isTransferring}
          transferProgress01={transferProgress01}
          transferOperation={transferOperation}
          status={status}
          showLabel={false}
          opacity={opacity}
          tint={tint}
        />
      );
      break;
    case 'CONVEYOR':
      body = (
        <Conveyor
          width={width}
          height={height}
          depth={depth}
          foupPresent={foupPresent}
          status={status}
          showLabel={false}
          opacity={opacity}
        />
      );
      break;
    case 'AMHS_INTERFACE':
    default: {
      body = (
        <group position={[0, 0, 0]}>
          <CleanroomWorkcell3D
            id={resolvedLabel}
            width={width}
            height={height}
            depth={depth}
            status={status}
            foupPresent={foupPresent}
            opacity={opacity}
          />
        </group>
      );
      void layoutElement;
      void loadPortCount;
      void occupiedLoadPorts;
      void flyingFoup;
      void portVisuals;
      void tint;
    }
  }

  return (
    <AmhsInteractiveGroup
      name={`AMHSEquipment-${type}`}
      position={position}
      rotation={rotation}
      scale={scale}
      selected={selected}
      onSelect={onSelect}
      highlightRadius={Math.max(width, depth) * 0.55}
    >
      {body}
      <AmhsLabel text={statusLabel} position={labelPosition} visible={showLabel} />
    </AmhsInteractiveGroup>
  );
};

export default AMHSEquipment;
